import { DialogComponent } from "@theme/dialog";
import { stockState } from "@theme/product-purchase-model";

const normalise = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ],
  );
const readJSON = (node) => JSON.parse(node?.textContent || "{}");
const expandMix = (value) =>
  String(value || "")
    .split(/\s*\+\s*/)
    .flatMap((part) => {
      const match = part.trim().match(/^(\d+)\s*x\s+(.+)$/i);
      return Array.from(
        { length: match ? Math.min(100, Number(match[1])) : 1 },
        () => (match ? match[2].trim() : part.trim()),
      );
    });
const signature = (flavors) => flavors.map(normalise).sort().join("|");

class PurchaseDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.trigger = this.querySelector(".purchase-dialog__trigger");
    this.addEventListener("dialog:open", this.onOpen);
    this.addEventListener("dialog:close", this.onClose);
    this.refs.dialog.addEventListener("cancel", this.onCancel);
  }

  onOpen = () => this.trigger?.setAttribute("aria-expanded", "true");
  onClose = () => this.trigger?.setAttribute("aria-expanded", "false");
  onCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  disconnectedCallback() {
    // Theme Editor can remove a block while its dialog has locked body scrolling.
    this.closeDialog();
    this.removeEventListener("dialog:open", this.onOpen);
    this.removeEventListener("dialog:close", this.onClose);
    this.refs.dialog?.removeEventListener("cancel", this.onCancel);
    super.disconnectedCallback();
  }

  showDialog() {
    if (this.opening || this.refs.dialog.open) return;
    this.opening = true;
    super.showDialog();
    requestAnimationFrame(() => {
      this.opening = false;
      if (!this.isConnected) this.closeDialog();
    });
  }

  // DialogComponent binds its close method as an instance field.
  constructor() {
    super();
    const close = this.closeDialog;
    this.closeDialog = async () => {
      if (this.closing || !this.refs.dialog?.open) return;
      this.closing = true;
      await close();
      this.closing = false;
      this.onClose();
      if (this.trigger?.isConnected)
        this.trigger.focus({ preventScroll: true });
    };
  }
}

class PurchaseTooltip extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.trigger = this.querySelector(".purchase-tooltip__toggle");
    this.panel = this.querySelector(".purchase-tooltip__panel");
    if (!this.trigger || !this.panel) return;
    this.clickTarget = this.classList.contains("purchase-tooltip--gift")
      ? this.closest("[data-purchase-gift]") || this
      : this.closest(".purchase-plan__features > li") || this.trigger;
    this.clickTarget.addEventListener(
      "click",
      (event) => {
        if (this.panel.contains(event.target)) return;
        const open = this.panel.hidden;
        document
          .querySelectorAll("purchase-tooltip")
          .forEach((tooltip) => tooltip.close());
        this.panel.hidden = !open;
        this.trigger.setAttribute("aria-expanded", String(open));
      },
      { signal },
    );
    document.addEventListener(
      "click",
      (event) => {
        if (
          !this.contains(event.target) &&
          !this.clickTarget.contains(event.target)
        )
          this.close();
      },
      { signal },
    );
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Escape" || this.panel.hidden) return;
        this.close();
        this.trigger.focus({ preventScroll: true });
      },
      { signal },
    );
    this.closest("purchase-supply")?.addEventListener(
      "change",
      () => this.close(),
      { signal },
    );
  }

  close() {
    if (!this.panel) return;
    this.panel.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}

if (!customElements.get("purchase-tooltip"))
  customElements.define("purchase-tooltip", PurchaseTooltip);

class PurchaseSupply extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    queueMicrotask(() => {
      if (!this.isConnected || signal.aborted) return;
      try {
        this.initialise();
      } catch (error) {
        console.error("Purchase configuration could not be loaded", error);
        this.showError(
          "Purchase options could not be loaded. Please refresh the page.",
        );
        this.setAvailable(false);
      }
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.form?.purchaseController !== this) return;
    delete this.form.purchaseController;
    delete this.form.dataset.purchaseAvailable;
    delete this.dataset.purchaseSupplyActive;
    this.form
      .querySelectorAll("[data-purchase-field]")
      .forEach((field) => field.remove());
    this.originalFields?.forEach((state, field) => {
      field.disabled = state.disabled;
      field.value = state.value;
    });
    const button = this.form.querySelector('[name="add"]');
    if (button) button.disabled = this.originalButtonDisabled;
    this.put(this.form, ".add-to-cart-text__content", this.originalButtonLabel);
    const accelerated = this.form.querySelector("[data-purchase-accelerated]");
    if (accelerated) accelerated.hidden = this.originalAcceleratedHidden;
  }

  initialise() {
    this.form = this.closest("form");
    if (!this.form) return;
    this.product = readJSON(this.querySelector("[data-purchase-product]"));
    this.settings = readJSON(this.querySelector("[data-purchase-settings]"));
    this.supplyIndex = this.product.optionNames.findIndex(
      (name) => normalise(name) === normalise(this.settings.supplyOption),
    );
    this.flavorIndex = this.product.optionNames.findIndex(
      (name) => normalise(name) === normalise(this.settings.flavorOption),
    );
    if (this.supplyIndex < 0 || this.flavorIndex < 0) {
      this.hidden = !window.Shopify?.designMode;
      this.showError(
        "Subscribe / Supply requires the configured Supply and Flavor product options.",
      );
      return;
    }
    if (this.form.purchaseController && this.form.purchaseController !== this) {
      this.hidden = !window.Shopify?.designMode;
      this.showError("Use one Subscribe / Supply block per product form.");
      return;
    }
    this.form.purchaseController = this;
    this.addEventListener("purchase:addon-change", () => this.update(), {
      signal: this.abort.signal,
    });
    this.originalFields = new Map(
      [
        ...this.form.querySelectorAll(
          '[name="id"], [name="quantity"], [name="selling_plan"]',
        ),
      ].map((field) => [
        field,
        { disabled: field.disabled, value: field.value },
      ]),
    );
    this.originalButtonDisabled =
      this.form.querySelector('[name="add"]')?.disabled;
    this.originalButtonLabel =
      this.form
        .querySelector(".add-to-cart-text__content")
        ?.textContent.trim() || "Add to cart";
    this.buttonLabel =
      this.form.querySelector("[data-add-to-cart-label]")?.dataset
        .addToCartLabel ||
      this.form.dataset.purchaseButtonLabel ||
      "Add to cart";
    this.originalAcceleratedHidden = this.form.querySelector(
      "[data-purchase-accelerated]",
    )?.hidden;
    this.dataset.purchaseSupplyActive = "true";
    this.memories = new Map();
    const query = new URLSearchParams(window.location.search);
    const requestedVariant = this.product.variants.find(
      (variant) => String(variant.id) === query.get("variant"),
    );
    const requestedPlan = query.get("selling_plan");
    this.plans = [...this.querySelectorAll("[data-purchase-plan]")].map(
      (element) => {
        const config = readJSON(
          element.querySelector("[data-purchase-plan-config]"),
        );
        const variants = this.product.variants.filter(
          (variant) =>
            normalise(variant.options[this.supplyIndex]) ===
              normalise(config.supply_value) &&
            expandMix(variant.options[this.flavorIndex]).length ===
              Number(config.packs),
        );
        const key = `${normalise(config.supply_value)}:${config.packs}`;
        const preferredTitle =
          Number(config.packs) > 1
            ? this.settings.defaultThree
            : this.settings.defaultOne;
        const plan = {
          element,
          config,
          variants,
          key,
          id: element.querySelector("input").value,
          selectedPlanId: null,
        };
        const isEligible = (variant) =>
          this.variantAvailable(variant) &&
          (config.mode === "onetime"
            ? !this.product.requiresSellingPlan
            : this.allocations(plan, variant).length > 0);
        const preferred =
          variants.find(
            (variant) => variant === requestedVariant && isEligible(variant),
          ) ||
          variants.find(
            (variant) =>
              normalise(variant.title) === normalise(preferredTitle) &&
              isEligible(variant),
          ) ||
          variants.find(isEligible) ||
          variants[0];
        if (!this.memories.has(key))
          this.memories.set(
            key,
            preferred ? expandMix(preferred.options[this.flavorIndex]) : [],
          );
        const radio = element.querySelector("[data-purchase-plan-radio]");
        radio.name = `purchase-plan-${this.form.getAttribute("id")}`;
        plan.gifts = [
          ...element.querySelectorAll("[data-purchase-gift]"),
        ].filter((gift) => gift.dataset.giftTitle);
        return plan;
      },
    );

    let selected;
    if (requestedVariant && requestedPlan) {
      selected = this.plans.find((plan) => {
        const allocation = this.allocations(plan, requestedVariant).find(
          (allocation) => this.matchesPlanId(allocation, requestedPlan),
        );
        if (
          !plan.variants.includes(requestedVariant) ||
          !allocation ||
          !this.variantAvailable(requestedVariant)
        )
          return false;
        this.memories.set(
          plan.key,
          expandMix(requestedVariant.options[this.flavorIndex]),
        );
        plan.selectedPlanId = String(allocation.id);
        return true;
      });
    }
    if (!selected && requestedVariant && !requestedPlan) {
      selected = this.plans.find(
        (plan) =>
          plan.config.mode === this.settings.defaultMode &&
          plan.variants.includes(requestedVariant) &&
          this.canSelect(plan),
      );
    }
    selected ||= this.plans.find(
      (plan) =>
        Number(plan.config.days) === Number(this.settings.defaultDays) &&
        plan.config.mode === this.settings.defaultMode &&
        this.canSelect(plan),
    );
    selected ||= this.plans.find((plan) => this.canSelect(plan));
    this.selectPlan(selected || this.plans[0]);

    const { signal } = this.abort;
    this.addEventListener(
      "change",
      (event) => {
        const radio = event.target.closest("[data-purchase-plan-radio]");
        if (!radio) return;
        const plan = this.plans.find((plan) => plan.id === radio.value);
        if (plan && this.canSelect(plan)) {
          this.selectPlan(plan);
          this.update(true);
        }
      },
      { signal },
    );
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[data-purchase-flavor]");
        if (!button || button.disabled || !this.selected) return;
        const flavors = this.flavors(this.selected).slice();
        flavors[Number(button.dataset.slot)] = button.dataset.purchaseFlavor;
        const variant = this.variantFor(this.selected, flavors);
        if (!this.availableForPlan(this.selected, variant)) return;
        this.memories.set(this.selected.key, flavors);
        this.update(true);
        this.querySelectorAll("[data-purchase-flavor]").forEach((next) => {
          if (
            next.dataset.slot === button.dataset.slot &&
            next.dataset.purchaseFlavor === button.dataset.purchaseFlavor
          )
            next.focus({ preventScroll: true });
        });
      },
      { signal },
    );
    this.form.addEventListener(
      "submit",
      (event) => {
        if (!this.selectionAvailable()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        this.syncForm();
      },
      { capture: true, signal },
    );
    this.form.addEventListener(
      "formdata",
      (event) => {
        const variant = this.variantFor(this.selected);
        const allocation = this.allocation(this.selected);
        event.formData.set("quantity", "1");
        if (variant) event.formData.set("id", String(variant.id));
        if (allocation)
          event.formData.set("selling_plan", String(allocation.id));
        else event.formData.delete("selling_plan");
        event.formData.delete(`purchase-plan-${this.form.getAttribute("id")}`);
      },
      { signal },
    );
    document.addEventListener(
      "shopify:block:select",
      (event) => {
        const plan = this.plans.find((plan) =>
          plan.element
            .closest("[data-shopify-editor-block]")
            ?.contains(event.target),
        );
        if (plan && this.canSelect(plan)) {
          this.selectPlan(plan);
          this.update(false);
        }
      },
      { signal },
    );
    this.update(false);
  }

  matchesPlanId(allocation, value) {
    return (
      String(allocation.id) === String(value) ||
      allocation.legacyIds?.some((id) => String(id) === String(value))
    );
  }

  allocations(plan, variant) {
    if (!plan || plan.config.mode !== "subscription" || !variant) return [];
    const allocations = variant.sellingPlans || [];
    const wanted = normalise(plan.config.selling_plan_match);
    if (wanted)
      return allocations.filter(
        (allocation) =>
          normalise(allocation.name) === wanted ||
          this.matchesPlanId(allocation, wanted),
      );
    const days = Number(plan.config.days);
    return allocations.filter((allocation) => {
      const cadence = allocation.name.match(
        /\b(\d+)\s*(days?|weeks?|months?)\b/i,
      );
      if (!cadence) return false;
      const count = Number(cadence[1]);
      const unit = cadence[2].toLowerCase();
      if (unit.startsWith("day")) return count === days;
      if (unit.startsWith("month")) return count * 30 === days;
      return (
        count * 7 === days || (days % 30 === 0 && count === (days / 30) * 4)
      );
    });
  }

  flavors(plan) {
    return this.memories.get(plan?.key) || [];
  }
  variantFor(plan, flavors = this.flavors(plan)) {
    return plan?.variants.find(
      (variant) =>
        signature(expandMix(variant.options[this.flavorIndex])) ===
        signature(flavors),
    );
  }
  sharedStock(variant) {
    return stockState(
      {
        product: this.product,
        country: this.product.country,
        stockRules: this.product.stockRules,
        variantRules: { [variant?.id]: { rules: variant?.stockRules } },
      },
      variant,
    );
  }
  variantAvailable(variant) {
    return (
      !this.sharedStock(variant).soldOut &&
      !!variant?.available &&
      Number(variant.quantityMin) <= 1 &&
      Number(variant.quantityIncrement) <= 1
    );
  }
  availableForPlan(plan, variant) {
    return (
      this.variantAvailable(variant) &&
      (plan.config.mode === "subscription"
        ? this.allocations(plan, variant).length > 0
        : !this.product.requiresSellingPlan)
    );
  }
  candidateVariant(plan) {
    const current = this.variantFor(plan);
    return this.availableForPlan(plan, current)
      ? current
      : plan?.variants.find((variant) =>
          this.availableForPlan(plan, variant),
        ) ||
          current ||
          plan?.variants[0];
  }
  canSelect(plan) {
    return !!plan && this.availableForPlan(plan, this.candidateVariant(plan));
  }
  selectionAvailable() {
    return (
      !!this.selected &&
      this.availableForPlan(this.selected, this.variantFor(this.selected))
    );
  }
  selectPlan(plan) {
    this.selected = plan;
    if (!plan || this.selectionAvailable()) return;
    const variant = this.candidateVariant(plan);
    if (variant)
      this.memories.set(plan.key, expandMix(variant.options[this.flavorIndex]));
  }
  allocation(plan, variant = this.variantFor(plan)) {
    const allocations = this.allocations(plan, variant);
    return (
      allocations.find((allocation) =>
        this.matchesPlanId(allocation, plan.selectedPlanId),
      ) ||
      allocations[0] ||
      null
    );
  }
  money(value, precise = false) {
    return new Intl.NumberFormat(this.product.locale || "en", {
      style: "currency",
      currency: this.product.currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: precise || Number(value) % 100 !== 0 ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(Number(value) / 100);
  }
  price(plan) {
    const variant =
      plan === this.selected
        ? this.variantFor(plan)
        : this.candidateVariant(plan);
    const allocation = this.allocation(plan, variant);
    return {
      variant,
      allocation,
      amount: allocation
        ? Number(allocation.price)
        : Number(variant?.price || 0),
      compare: Number(
        allocation?.compareAtPrice ??
          variant?.compareAtPrice ??
          variant?.price ??
          0,
      ),
    };
  }
  renewal(plan) {
    const allocation = this.allocation(
      plan,
      plan === this.selected
        ? this.variantFor(plan)
        : this.candidateVariant(plan),
    );
    if (!allocation) return "";
    const cadence = allocation.name.match(
      /\b(\d+)\s*(days?|weeks?|months?)\b/i,
    );
    const count = cadence && Number(cadence[1]);
    const unit = cadence?.[2].toLowerCase().replace(/s$/, "");
    const interval = cadence
      ? `every ${count === 1 ? "" : `${count} `}${unit}${count === 1 ? "" : "s"}`
      : `· ${allocation.name}`;
    const renewalPrice = allocation.recurringPrice ?? allocation.price;
    return this.settings.renewalLabel
      .replace("{price}", this.money(renewalPrice))
      .replace("{interval}", interval);
  }
  giftSummary(plan, orderSummary = false) {
    const gifts = plan?.gifts.filter((gift) => gift.dataset.giftProduct) || [];
    if (!gifts.length) return "";
    let label = orderSummary
      ? `${gifts.length} FREE ${gifts.length === 1 ? "gift" : "gifts"}`
      : this.settings.giftsLabel.replace("{count}", String(gifts.length));
    if (
      gifts.every(
        (gift) =>
          gift.hasAttribute("data-gift-value") &&
          Number.isFinite(Number(gift.dataset.giftValue)),
      )
    ) {
      const value = this.money(
        gifts.reduce(
          (total, gift) => total + Number(gift.dataset.giftValue),
          0,
        ),
      );
      label += orderSummary ? ` — ${value} total value` : ` (${value} Value)`;
    }
    return label;
  }
  put(root, selector, text, hideEmpty = false) {
    const node = root.querySelector(selector);
    if (!node) return;
    node.textContent = text;
    if (hideEmpty) node.hidden = !text;
  }

  update(announce) {
    this.plans.forEach((plan) => {
      const { variant, allocation, amount, compare } = this.price(plan);
      const validPrice =
        !!variant && (plan.config.mode === "onetime" || !!allocation);
      const active = plan === this.selected;
      const available = this.canSelect(plan);
      const radio = plan.element.querySelector("[data-purchase-plan-radio]");
      radio.checked = active;
      radio.disabled = !available;
      plan.element.classList.toggle("is-selected", active);
      plan.element.classList.toggle("is-unavailable", !available);
      const details = plan.element.querySelector("[data-plan-details]");
      const hasOffer =
        plan.gifts.length > 0 ||
        [...details.querySelectorAll(".purchase-plan__features > li")].some(
          (item) => item.textContent.trim(),
        );
      details.hidden = !active || !available || !hasOffer;
      this.put(
        plan.element,
        "[data-plan-price]",
        validPrice ? this.money(amount) : this.settings.unavailableLabel,
      );
      this.put(
        plan.element,
        "[data-plan-compare]",
        validPrice && compare > amount ? this.money(compare) : "",
        true,
      );
      this.put(
        plan.element,
        "[data-plan-daily]",
        validPrice && Number(plan.config.days) > 0
          ? `${this.money(amount / Number(plan.config.days), true)}/day`
          : "",
      );
      this.put(
        plan.element,
        "[data-plan-saving]",
        validPrice && compare > amount
          ? `SAVE ${Math.round((1 - amount / compare) * 100)}%`
          : "",
        true,
      );
      this.put(plan.element, "[data-plan-renewal]", this.renewal(plan));
      this.put(
        plan.element,
        "[data-plan-gift-label]",
        this.giftSummary(plan),
        true,
      );
    });
    const plan = this.selected;
    this.showError(
      this.selectionAvailable()
        ? ""
        : "This plan or flavor combination is unavailable. Please choose another option.",
    );
    if (!plan) {
      this.put(
        this.closest(".product-details"),
        "[data-purchase-from]",
        "",
        true,
      );
      this.querySelector("[data-purchase-summary]").hidden = true;
      this.querySelector("[data-purchase-flavors-section]").hidden = true;
      this.syncForm();
      return;
    }
    this.renderFlavors();
    const { variant, amount, compare, allocation } = this.price(plan);
    const summary = this.querySelector("[data-purchase-summary]");
    summary.hidden =
      !variant || (plan.config.mode === "subscription" && !allocation);
    const addon = this.querySelector("product-purchase-addon")?.summary?.();
    const firstOrderPrice = amount + (addon?.price || 0);
    const firstOrderCompare = compare + (addon?.compare || 0);
    this.put(summary, "[data-summary-price]", this.money(firstOrderPrice));
    this.put(
      summary,
      "[data-summary-compare]",
      firstOrderCompare > firstOrderPrice ? this.money(firstOrderCompare) : "",
      true,
    );
    this.put(
      summary,
      "[data-summary-plan]",
      `${plan.config.title} | ${plan.config.summary_description || plan.config.pack_description}`,
    );
    const flavors = new Map();
    this.flavors(plan).forEach((flavor) =>
      flavors.set(flavor, (flavors.get(flavor) || 0) + 1),
    );
    this.put(
      summary,
      "[data-summary-flavors]",
      [...flavors].map(([flavor, count]) => `${flavor} x${count}`).join(", "),
    );
    this.put(
      summary,
      "[data-summary-renewal]",
      allocation
        ? this.renewal(plan)
        : this.settings.oneTimeDelivery.replace("[price]", this.money(amount)),
      true,
    );
    let addonLine = summary.querySelector("[data-summary-addon]");
    if (!addonLine) {
      addonLine = document.createElement("p");
      addonLine.dataset.summaryAddon = "";
    }
    summary.append(addonLine);
    addonLine.textContent = addon?.text || "";
    addonLine.hidden = !addon;
    this.put(
      summary,
      "[data-summary-gifts]",
      this.giftSummary(plan, true),
      true,
    );
    const days = Number(plan.config.days);
    const dailyPrice =
      this.selectionAvailable() && days > 0 && Number.isFinite(amount)
        ? `From ${this.money(amount / days, true)}/day`
        : "";
    this.put(
      this.closest(".product-details"),
      "[data-purchase-from]",
      dailyPrice,
      true,
    );
    this.syncForm();
    // Keep the existing tagged gallery integration, including its startup snapshot.
    this.__kbSub = {
      selectedId: plan.id,
      options: this.plans.map((item) => ({
        id: item.id,
        kind: item.config.mode,
        days: Number(item.config.days),
        title: item.config.title,
        selectedVariant: this.variantFor(item),
      })),
    };
    if (variant) {
      this.dispatchEvent(
        new CustomEvent("kb-sub:option-change", {
          bubbles: true,
          detail: {
            id: plan.id,
            kind: plan.config.mode,
            days: Number(plan.config.days),
            title: plan.config.title,
            variantId: variant.id,
            variantTitle: variant.options[this.flavorIndex],
            mediaId: variant.mediaId,
            image: variant.image,
          },
        }),
      );
      const scope = this.closest(".product-details");
      const price = scope?.querySelector("product-price");
      if (price && (allocation || plan.config.mode === "onetime")) {
        this.put(price, '[ref="priceContainer"] .price', this.money(amount));
        const comparePrice = price.querySelector(".compare-at-price");
        if (comparePrice) {
          comparePrice.textContent =
            compare > amount ? this.money(compare) : "";
          (comparePrice.closest('[role="group"]') || comparePrice).hidden =
            compare <= amount;
        }
        price.dataset.variantId = String(variant.id);
        price.classList.add("price-ready");
      }
      const installments = scope?.querySelector(".installments");
      if (installments)
        installments.textContent = `${plan.config.days} day supply / ${this.money(amount / Math.max(1, Number(plan.config.days)))} per day`;
    }
    if (announce) {
      if (variant && this.selectionAvailable() && !window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", String(variant.id));
        if (allocation)
          url.searchParams.set("selling_plan", String(allocation.id));
        else url.searchParams.delete("selling_plan");
        window.history.replaceState(window.history.state, "", url);
      }
      this.dispatchEvent(
        new CustomEvent("purchase:change", {
          bubbles: true,
          detail: {
            variantId: variant?.id,
            sellingPlanId: allocation?.id || null,
            price: amount,
            packs: Number(plan.config.packs),
          },
        }),
      );
    }
  }

  renderFlavors() {
    const plan = this.selected;
    const selectedFlavors = this.flavors(plan);
    const choices = [
      ...new Set(
        plan.variants.flatMap((variant) =>
          expandMix(variant.options[this.flavorIndex]),
        ),
      ),
    ];
    this.querySelector("[data-purchase-flavors-section]").hidden =
      !selectedFlavors.length;
    const flavorImage = (flavor) => {
      if (normalise(flavor) === normalise(this.settings.orangeName))
        return this.settings.orangeImage;
      if (normalise(flavor) === normalise(this.settings.citrusName))
        return this.settings.citrusImage;
      return null;
    };
    this.querySelector("[data-purchase-flavors]").innerHTML = selectedFlavors
      .map((flavor, slot) => {
        const isFree =
          Number(plan.config.free_packs) > 0 &&
          slot >= Number(plan.config.packs) - Number(plan.config.free_packs);
        return `<fieldset class="purchase-flavors"><legend>${selectedFlavors.length > 1 ? `${escapeHtml(this.settings.packLabel)} ${slot + 1}` : '<span class="visually-hidden">Flavor</span>'}${isFree ? ` <span class="purchase-plan__saving">${escapeHtml(this.settings.freeLabel)}</span>` : ""}</legend><div class="purchase-flavors__options">${choices
          .map((choice) => {
            const next = selectedFlavors.slice();
            next[slot] = choice;
            const available = this.availableForPlan(
              plan,
              this.variantFor(plan, next),
            );
            const url = flavorImage(choice);
            return `<button type="button" class="purchase-flavor" data-purchase-flavor="${escapeHtml(choice)}" data-slot="${slot}" aria-pressed="${normalise(choice) === normalise(flavor)}" ${available ? "" : "disabled"}>${url ? `<img src="${escapeHtml(url)}" width="24" height="24" alt="">` : ""}<span>${escapeHtml(choice)}</span></button>`;
          })
          .join("")}</div></fieldset>`;
      })
      .join("");
    const backorder = this.settings.backorderFlavor;
    const backorderNames =
      backorder === "both"
        ? [this.settings.orangeName, this.settings.citrusName]
        : [
            backorder === "orange blast"
              ? this.settings.orangeName
              : backorder === "citrus apple"
                ? this.settings.citrusName
                : "",
          ];
    const showBackorder = selectedFlavors.some((flavor) =>
      backorderNames.map(normalise).includes(normalise(flavor)),
    );
    const sharedMessages = this.sharedStock(this.variantFor(plan)).messages;
    this.put(
      this,
      "[data-purchase-backorder]",
      sharedMessages.length
        ? sharedMessages.join(" ")
        : showBackorder
          ? this.settings.backorderMessage
          : "",
      true,
    );
  }

  setAvailable(available) {
    if (!this.form) return;
    this.form.dataset.purchaseAvailable = String(available);
    const button = this.form.querySelector('[name="add"]');
    if (button)
      button.disabled =
        !available || this.form.dataset.purchasePending === "true";
    this.put(
      this.form,
      ".add-to-cart-text__content",
      available
        ? this.buttonLabel
        : this.settings?.unavailableLabel || "Unavailable",
    );
    const accelerated = this.form.querySelector("[data-purchase-accelerated]");
    if (accelerated) accelerated.hidden = !available;
  }

  syncForm() {
    if (!this.form) return;
    const variant = this.variantFor(this.selected);
    const allocation = this.allocation(this.selected);
    const input = (name) => {
      let field = this.form.querySelector(
        `input[data-purchase-field="${name}"]`,
      );
      if (!field) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = name;
        field.dataset.purchaseField = name;
        this.form.append(field);
      }
      return field;
    };
    const variantField = this.form.querySelector('[name="id"]');
    if (variantField) variantField.value = variant ? String(variant.id) : "";
    const quantity = input("quantity");
    const preserveField = (field) => {
      if (
        !field.hasAttribute("data-purchase-field") &&
        !this.originalFields.has(field)
      ) {
        this.originalFields.set(field, {
          disabled: field.disabled,
          value: field.value,
        });
      }
    };
    this.form.querySelectorAll('[name="quantity"]').forEach((field) => {
      preserveField(field);
      field.disabled = field !== quantity;
    });
    quantity.value = "1";
    const sellingPlan = input("selling_plan");
    this.form.querySelectorAll('[name="selling_plan"]').forEach((field) => {
      preserveField(field);
      field.disabled = field !== sellingPlan || !allocation;
    });
    sellingPlan.value = allocation ? String(allocation.id) : "";
    const button = this.form.querySelector("add-to-cart-component");
    if (button && variant?.image)
      button.dataset.productVariantMedia = variant.image;
    this.setAvailable(this.selectionAvailable());
  }

  showError(message) {
    this.put(this, "[data-purchase-error]", message, true);
  }
}

if (!customElements.get("purchase-dialog"))
  customElements.define("purchase-dialog", PurchaseDialog);
if (!customElements.get("purchase-supply"))
  customElements.define("purchase-supply", PurchaseSupply);

// A standalone Buy buttons block also works when no separate variant picker is present.
document.addEventListener("change", (event) => {
  const select = event.target.closest("[data-purchase-native-variant]");
  if (!select) return;
  const form = select.closest("form");
  if (!form || form.purchaseController) return;
  const url = new URL(window.location.href);
  url.searchParams.set("variant", select.value);
  window.location.assign(url.toString());
});
